import { React, moment } from 'Imports';
import { useState } from 'react';
import { isEncompass } from '$Utilities/integrationPlatform';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import { Moment } from 'moment-timezone';

export type ParsedRecallTime = { hour: number; minute: number; second: number; amPm: 'AM' | 'PM' };

interface RecallTimeInputProps {
    // The absolute instant currently selected; displayed converted to `timezone`.
    value?: Date;
    // IANA zone the footage was recorded in -- entry and display both use this zone.
    timezone: string;
    // Instant the abbreviation is resolved at, so it reflects whether the FOOTAGE was recorded in standard
    // or daylight time rather than whether today is. Omit to suppress the suffix (zone not known).
    abbreviationAt?: Date;
    error?: boolean;
    // Fired on every edit: a fully-parsed time, or undefined while the sections are incomplete.
    onTimeChange: (time: ParsedRecallTime | undefined) => void;
    // Fired when editing ends, with the final parse result -- lets the parent flag abandoned incomplete entry.
    onEditEnd?: (time: ParsedRecallTime | undefined) => void;
}

const toParsedTime = (value: Moment | null): ParsedRecallTime | undefined => {
    if (!value || !value.isValid()) return undefined;
    const hour24 = value.hours();
    return {
        hour: hour24 % 12 === 0 ? 12 : hour24 % 12,
        minute: value.minutes(),
        second: value.seconds(),
        amPm: hour24 < 12 ? 'AM' : 'PM',
    };
};

// Segmented HH:MM:SS AM/PM entry (MUI TimeField) with the (non-editable) footage timezone abbreviation
// right-aligned inside the field. Semi-controlled: while the user is editing, their draft wins; outside of
// editing, the displayed time follows `value` (which the video preview updates as footage is played/scrubbed).
export const RecallTimeInput = (props: RecallTimeInputProps) => {
    const { value, timezone } = props;

    const [draft, setDraft] = useState<Moment | null>(null);
    const [isFocused, setIsFocused] = useState(false);

    const externalValue = value ? moment(value).tz(timezone) : null;
    const fieldValue = isFocused ? draft : externalValue;
    const errorColor = props.error ? (isEncompass ? '#811b1f' : '#981818') : undefined;

    const timezoneAbbr = props.abbreviationAt ? moment(props.abbreviationAt).tz(timezone).format('z') : null;

    const handleFocus = () => {
        setDraft(externalValue);
        setIsFocused(true);
    };

    const handleBlur = () => {
        setIsFocused(false);
        props.onEditEnd?.(toParsedTime(draft));
        setDraft(null);
    };

    const handleChange = (newValue: Moment | null) => {
        setDraft(newValue);
        props.onTimeChange(toParsedTime(newValue));
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <TimeField
                value={fieldValue}
                timezone={timezone}
                // The app theme defaults every MuiTextField to the borderless 'standard' variant; this
                // field must be outlined to pair with the Recall Duration dropdown.
                variant="outlined"
                format="hh:mm:ss A"
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                fullWidth
                InputProps={{
                    endAdornment: timezoneAbbr ? (
                        <span
                            style={{
                                color: isEncompass ? '#878B8C' : '#4E677E',
                                fontSize: '14px',
                                fontWeight: 400,
                                userSelect: 'none',
                            }}
                        >
                            {timezoneAbbr}
                        </span>
                    ) : undefined,
                }}
                // Border, radius (theme default), and paddings mirror SingleSelect so the field and the
                // Recall Duration dropdown next to it read as one control pair. TimeField owns the MUI
                // error state internally, so the error border is driven from our prop instead.
                sx={{
                    '& .VP-MuiInputBase-root': {
                        height: isEncompass ? '36px' : '40px',
                        fontSize: '14px',
                        color: isEncompass ? '#373e40' : '#202328',
                        backgroundColor: '#ffffff',
                        paddingRight: '12px',
                    },
                    '& .VP-MuiInputBase-input': {
                        padding: '0 0 0 12px',
                    },
                    '& .VP-MuiOutlinedInput-notchedOutline, & .VP-MuiInputBase-root:hover .VP-MuiOutlinedInput-notchedOutline': {
                        borderColor: errorColor ?? (isEncompass ? '#c4cbd4' : '#c4cbd7'),
                        borderWidth: '1px',
                    },
                    '& .VP-MuiInputBase-root.Mui-focused .VP-MuiOutlinedInput-notchedOutline': {
                        borderColor: errorColor ?? (isEncompass ? '#0d7ac5' : '#25477b'),
                        borderWidth: '1px',
                    },
                }}
            />
        </LocalizationProvider>
    );
};

export default RecallTimeInput;
